import {
  expectedNote,
  statementFigure as figure,
  GUEST_NOTE,
  notCovered,
  planTerms,
  runwaySentence,
  SEPARATE_NOTE,
  STATEMENT_ACTIVITY,
  STATEMENT_COLUMNS,
  STATEMENT_GIFT_NOTE,
  STATEMENT_PAY_BY_HAND,
  STATEMENT_STARTING_BALANCE,
  STATEMENT_STILL_OPEN,
  separatePledgeTerms,
  standingTerms,
  statementBalance,
  statementDay,
  statementGivenLabel,
  statementLineAmount,
  statementRunning,
  statementTotals,
} from '@app/shared'
import type { ReactNode } from 'react'
import type { RouterOutputs } from '../lib/trpc'
import { Figure } from './Figure'
import { Badge, Card } from './index'
import { Meter } from './Meter'
import { TotalLine } from './TotalLine'
import './statement.css'

type View = RouterOutputs['pay']['bill']

type Line = {
  key: string
  day?: string
  label: string
  note?: string | null
  amount?: string
  balance?: string
  quiet?: boolean
  /** How far along a debt is, 0 to 1. */
  share?: number
}

/**
 * A billing account's statement, as the member reads it on the page the email
 * links to. The content is the email's own — one server builder, one set of
 * words — so this sets it and says nothing of its own.
 *
 * The figure leads; totals and the running account follow; what is not owed
 * is framed apart from the balance. The accent is spent once, on a figure
 * that asks for something, and never on an account in credit.
 */
export function Statement({ view }: { view: View }) {
  const b = statementBalance(view.netOwedCents)
  const owes = view.netOwedCents > 0
  const year = view.periodLabel.slice(-4)
  const when = (iso: string) => statementDay(iso, view.timeZone, year)

  return (
    <div className="statement">
      <Figure
        statement
        asks={owes}
        amount={b.figure}
        label={b.lead}
        note={view.netOwedCents < 0 ? 'Nothing is owed.' : undefined}
      />

      <div className="statement__totals">
        {statementTotals(view).map((t) => (
          <TotalLine
            key={t.label}
            emphasis={t.closing ? 'total' : undefined}
            label={t.label}
            amount={t.amount}
          />
        ))}
      </div>

      {view.lines.length ? (
        <Section label={STATEMENT_ACTIVITY}>
          <Lines
            ledger
            head={STATEMENT_COLUMNS}
            lines={[
              {
                key: 'opening',
                day: '',
                label: STATEMENT_STARTING_BALANCE,
                balance: statementRunning(view.openingCents),
              },
              ...view.lines.map((l, i) => {
                const given = l.kind === 'given'
                return {
                  key: `l${i}`,
                  day: when(l.at),
                  label: given ? statementGivenLabel(l.label) : l.label,
                  note: given ? STATEMENT_GIFT_NOTE : l.note,
                  amount: statementLineAmount(l),
                  balance: statementRunning(l.balanceCents),
                  quiet: given,
                }
              }),
            ]}
          />
        </Section>
      ) : null}

      {view.stillOpen.length ? (
        <Section label={STATEMENT_STILL_OPEN}>
          <Lines
            lines={view.stillOpen.map((c) => ({
              key: c.id,
              day: when(c.issuedAt),
              label: c.label,
              amount: figure(c.remainingCents),
            }))}
          />
        </Section>
      ) : null}

      {view.separatePledges.length ? (
        <Frame label="Pledges paid separately" note={SEPARATE_NOTE} owed>
          <Lines
            lines={view.separatePledges.map((p) => ({
              key: p.id,
              label: p.label,
              note: `${p.pledgedBy ? `Pledged by ${p.pledgedBy} · ` : ''}${separatePledgeTerms(p)}`,
              amount: figure(p.owedCents),
              share: p.amountCents > 0 ? p.paidCents / p.amountCents : 0,
            }))}
          />
        </Frame>
      ) : null}

      {view.expected.length ? (
        <Frame label="Expected" note={expectedNote(view.giftTo)}>
          <Lines
            quiet
            lines={view.expected.map((e, i) => ({
              key: `e${i}`,
              label: e.label,
              note: notCovered(e.periods, e.cadence),
              amount: figure(e.amountCents),
            }))}
          />
        </Frame>
      ) : null}

      {view.account.guestPledges.length ? (
        <Frame label="Pledged by your guests" note={GUEST_NOTE}>
          <Lines
            quiet
            lines={view.account.guestPledges.map((g, i) => ({
              key: `g${i}`,
              label: g.guestName,
              note: g.label,
              amount: figure(g.owedCents),
            }))}
          />
        </Frame>
      ) : null}

      {view.plans.length || view.standing.length ? (
        <Section label="Standing arrangements">
          <Lines
            lines={[
              ...view.plans.map((p, i) => ({
                key: `plan${i}`,
                label: p.label,
                note: planTerms(p),
                share:
                  p.totalCents && p.totalCents > 0
                    ? p.paidCents / p.totalCents
                    : undefined,
              })),
              ...view.standing.map((a, i) => ({
                key: `s${i}`,
                label: a.label,
                note: standingTerms(a),
              })),
            ]}
          />
        </Section>
      ) : null}

      {view.runwayMonths !== null ? (
        <p className="statement__runway">{runwaySentence(view.runwayMonths)}</p>
      ) : null}
    </div>
  )
}

/**
 * The ways to pay that skip the page — Zelle, a check — as the statement's
 * last section. Its own piece so the page sets it after its payment form, the
 * way the email sets it after its Pay button: the other ways follow the one
 * the link is for.
 */
export function StatementPayByHand({ ways }: { ways: View['payByHand'] }) {
  if (ways.length === 0) return null
  return (
    <div className="statement">
      <Section label={STATEMENT_PAY_BY_HAND}>
        <Lines
          lines={ways.map((w) => ({
            key: w.label,
            label: w.label,
            note: w.details.join('\n'),
          }))}
        />
      </Section>
    </div>
  )
}

function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section className="statement__section">
      <h2 className="statement__label">{label}</h2>
      {children}
    </section>
  )
}

/**
 * What is shown and is not owed, framed apart and marked so. Set apart from the
 * balance. `owed` says the billing account still owes what it holds — a pledge
 * paid separately — so it carries no "Not owed" badge.
 */
function Frame({
  label,
  note,
  owed,
  children,
}: {
  label: string
  note: string
  owed?: boolean
  children: ReactNode
}) {
  return (
    <Card as="section" tone="sunken">
      <div className="statement__frame-head">
        <h2 className="statement__label">{label}</h2>
        {owed ? null : <Badge>Not owed</Badge>}
      </div>
      {children}
      <p className="statement__note">{note}</p>
    </Card>
  )
}

function Lines({
  lines,
  quiet,
  ledger,
  head,
}: {
  lines: Line[]
  quiet?: boolean
  ledger?: boolean
  /** Column names, one per column, drawn as the list's first row. */
  head?: readonly string[]
}) {
  const dated = lines.some((l) => l.day !== undefined)
  return (
    <ul
      className="statement__lines"
      data-quiet={quiet || undefined}
      data-dated={dated || undefined}
      data-ledger={ledger || undefined}
    >
      {head ? (
        <li className="statement__line statement__head" aria-hidden="true">
          {head.map((h) => (
            <span key={h}>{h}</span>
          ))}
        </li>
      ) : null}
      {lines.map((l) => (
        <li
          key={l.key}
          className="statement__line"
          data-quiet={l.quiet || undefined}
        >
          {dated ? <span className="statement__day">{l.day}</span> : null}
          <span className="statement__what">
            <span className="statement__name">{l.label}</span>
            {l.note ? <span className="statement__note">{l.note}</span> : null}
            {l.share !== undefined ? (
              <Meter share={l.share} label={`${l.label}, paid so far`} />
            ) : null}
          </span>
          {ledger ? (
            <span className="statement__figures">
              <span className="statement__amount">{l.amount}</span>
              <span className="statement__balance">{l.balance}</span>
            </span>
          ) : l.amount ? (
            <span className="statement__amount">{l.amount}</span>
          ) : null}
        </li>
      ))}
    </ul>
  )
}
